import propz from 'propz';
import { SchoolEvent } from 'Src/models/event';
import { EVENT_STATUS_SERVER_TO_CLIENT_MAPPING } from 'Src/consts/event';

const PENDING_INVITE_STATUSES = ['NOT_READY', 'PREBOOKED'];
const PENDING_EVENT_STATUSES = ['SENDING_INVITES', 'INVITES_SENT', 'COLLECTING_INVITE_RESPONSE'];

export type CalendarEventVisualStatusKind = 'cancelled' | 'awaitingSelf' | 'awaitingOpponent' | 'confirmed' | 'neutral';

export interface CalendarEventVisualStatus {
  kind: CalendarEventVisualStatusKind;
  label: string;
  eventClassName: string;
  badgeClassName: string;
}

function isPendingInvite(invite): boolean {
  return PENDING_INVITE_STATUSES.includes(invite.status);
}

function isSameId(value, expected): boolean {
  return typeof value !== 'undefined' && typeof expected !== 'undefined' && String(value) === String(expected);
}

export function getCalendarEventVisualStatus(event: SchoolEvent, activeSchoolId?: string): CalendarEventVisualStatus {
  if (event.status === 'CANCELED') {
    return {
      kind: 'cancelled',
      label: 'Cancelled',
      eventClassName: 'cal-ev--cancelled',
      badgeClassName: 'cal-badge--cancelled'
    };
  }

  const invites = propz.get(event, ['invites'], []);
  const isPendingEvent = PENDING_EVENT_STATUSES.includes(event.status);
  const isOwnSchoolInvited = propz
    .get(event, ['invitedSchoolIds'], [])
    .some(schoolId => isSameId(schoolId, activeSchoolId));
  const isOwnSchoolInviter = isSameId(event.inviterSchoolId, activeSchoolId);
  const isAwaitingSelf = invites.some(invite => isSameId(invite.invitedSchoolId, activeSchoolId) && isPendingInvite(invite))
    || (isPendingEvent && isOwnSchoolInvited && !isOwnSchoolInviter);

  if (isAwaitingSelf) {
    return {
      kind: 'awaitingSelf',
      label: 'Awaiting your confirmation',
      eventClassName: 'cal-ev--awaiting-self',
      badgeClassName: 'cal-badge--awaiting-self'
    };
  }

  const isAwaitingOpponent = invites.some(invite => isSameId(invite.inviterSchoolId, activeSchoolId) && isPendingInvite(invite))
    || (isPendingEvent && isOwnSchoolInviter);

  if (isAwaitingOpponent) {
    return {
      kind: 'awaitingOpponent',
      label: 'Awaiting opponent',
      eventClassName: 'cal-ev--awaiting-opponent',
      badgeClassName: 'cal-badge--awaiting-opponent'
    };
  }

  if (event.status === 'ACCEPTED') {
    return {
      kind: 'confirmed',
      label: 'Confirmed',
      eventClassName: 'cal-ev--confirmed',
      badgeClassName: 'cal-badge--success'
    };
  }

  return {
    kind: 'neutral',
    label: EVENT_STATUS_SERVER_TO_CLIENT_MAPPING[event.status] || event.status,
    eventClassName: 'cal-ev--neutral-status',
    badgeClassName: ''
  };
}